<template>
  <div 
    v-if="artist" 
    class="min-h-screen transition-colors duration-300 mt-16"
    :style="{ backgroundColor: backgroundColor }"
  >
    <!-- Hero Section -->
    <section 
      class="z-[1] relative border-b px-4 py-8 sm:px-6 lg:px-8 transition-colors"
      :class="[textColorClass, borderColorClass]"
    >
      <div class="max-w-4xl">
        <div class="flex flex-col md:flex-row gap-12 items-start w-full">
          <!-- Artist Photo -->
          <div class="w-3/5 md:w-1/2 relative aspect-square border overflow-hidden" :class="borderColorClass">
            <template v-if="resolveForDisplay(artist?.photo, artist?.slug).provider">
            <NuxtImg
              v-bind="resolveForDisplay(artist?.photo, artist?.slug)"
                :provider="resolveForDisplay(artist?.photo, artist?.slug).provider"
                :alt="artist.name"
                class="object-cover"
                sizes="sm:360px md:480px lg:640px"
                :modifiers="{ quality: 'auto', format: 'auto' }"
              />
            </template>
            <img
              v-else
              :src="resolveForDisplay(artist?.photo, artist?.slug).src"
              :alt="artist.name"
              class="object-cover"
            />
          </div>
          <!-- Artist Info -->
          <div class="space-y-6 w-full md:w-2/3" :class="textColorClass">
            <h1 class="text-6xl" :class="textColorClass">{{ artist.name }}</h1>
            <p v-if="artist.tagline" class="text-xl leading-relaxed font-['Inter']" :class="textColorClass">{{ artist.tagline }}</p>
            <p v-if="typeof artist.bio === 'string' && artist.bio" class="text-base leading-relaxed font-['Inter']" :class="textMutedClass">{{ artist.bio }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tabs Navigation -->
    <section class="border-b transition-colors" :class="borderColorClass">
      <div class="mx-auto max-w-screen-2xl">
        <div class="flex gap-0">
          <button
            @click="activeTab = 'collaborations'"
            :class="[
              'flex-1 py-6 text-xl font-medium border-r-2 transition-colors',
              borderColorClass,
              activeTab === 'collaborations'
                ? activeTabClasses
                : inactiveTabClasses
            ]"
          >
            {{ t('pages.artist.tabs.collaborations') }}
            <span class="font-mono">({{ artistProducts.length }})</span>
          </button>
          <button
            @click="activeTab = 'about'"
            :class="[
              'flex-1 py-6 text-xl font-medium transition-colors',
              activeTab === 'about'
                ? activeTabClasses
                : inactiveTabClasses
            ]"
          >
            {{ t('pages.artist.tabs.about') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Collaborations Tab -->
    <section v-show="activeTab === 'collaborations'" class="px-4 py-8 sm:px-6 lg:px-8 bg-white">
      <div class="mx-auto max-w-screen-2xl">
        <div v-if="artistProducts.length === 0" class="text-center py-12">
          <p class="text-xl" :class="textColorClass">{{ t('pages.artist.collaborations.empty') }}</p>
        </div>
        <div v-else class="grid grid-cols-2 md:grid-cols-3 gap-8">
          <CollectionProductCard
            v-for="product in artistProducts"
            :key="product.id"
            :product="product"
          />
        </div>
      </div>
    </section>

    <!-- About Tab -->
    <section v-show="activeTab === 'about'" class="px-4 py-8 sm:px-6 lg:px-8">
      <div class="mx-auto max-w-screen-2xl space-y-24">
        <!-- Intro (from story) -->
        <div v-if="artist.story && artist.story.intro" class="max-w-3xl">
          <p class="text-xl leading-relaxed font-['Inter']" :class="textColorClass">
            {{ artist.story.intro }}
          </p>
        </div>

        <!-- Gallery -->
        <ArtistGallery
          v-if="artist.gallery && artist.gallery.length > 0"
          :images="artist.gallery"
          :text-color-class="textColorClass"
          :border-color-class="borderColorClass"
          :artist-slug="artist.slug"
              />

        <!-- Bio Sections (Story Format with sections) -->
        <template v-if="artist.story && artist.story.sections && artist.story.sections.length > 0">
          <div class="space-y-16">
            <div 
              v-for="(section, index) in artist.story.sections" 
              :key="index"
              class="max-w-3xl"
            >
              <h3 class="text-4xl mb-6 font-serif" :class="textColorClass">{{ section.title }}</h3>
              <p class="text-lg leading-relaxed font-['Inter']" :class="textMutedClass">{{ section.content }}</p>
            </div>
          </div>
        </template>

        <!-- Simple bio text (string) -->
        <div v-else-if="artist.bio" class="max-w-3xl">
          <p class="text-lg leading-relaxed font-['Inter']" :class="textMutedClass">{{ artist.bio }}</p>
        </div>

        <!-- Links -->
        <div v-if="artist.links && artist.links.length > 0">
          <h2 class="text-6xl mb-12 font-serif" :class="textColorClass">{{ t('pages.artist.links.title') }}</h2>
          <div class="grid md:grid-cols-3 gap-6">
            <a
              v-for="(link, index) in artist.links"
              :key="index"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="group border p-8 transition-colors flex items-center justify-between border-white text-white hover:bg-white hover:text-dark-teal-600"
            >
              <span class="text-2xl">{{ link.label }}</span>
              <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Discover More Artists -->
    <section class="border-t-2 px-6 py-16 md:px-12 md:py-24 transition-colors" :class="[borderColorClass, bgWhiteClass]">
      <div class="mx-auto max-w-screen-2xl">
        <h2 class="text-6xl mb-12 text-black">{{ t('pages.artist.discover.title') }}</h2>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          <NuxtLink
            v-for="otherArtist in otherArtists"
            :key="otherArtist.slug"
            :to="`/of/${otherArtist.slug}`"
            class="group border hover:border-4 transition-all border-black"
          >
            <div class="relative aspect-square border-b-2 border-black overflow-hidden">
              <template v-if="resolveForDisplay(otherArtist.photo, otherArtist.slug).provider">
                <NuxtImg
                  v-bind="resolveForDisplay(otherArtist.photo, otherArtist.slug)"
                  :provider="resolveForDisplay(otherArtist.photo, otherArtist.slug).provider"
                  :alt="otherArtist.name"
                  class="w-full h-full object-cover transition-transform group-hover:scale-110"
                  sizes="sm:160px md:200px lg:240px"
                  :modifiers="{ quality: 'auto', format: 'auto' }"
                />
              </template>
              <img
                v-else
                :src="resolveForDisplay(otherArtist.photo, otherArtist.slug).src"
                :alt="otherArtist.name"
                class="w-full h-full object-cover transition-transform group-hover:scale-110"
              />
            </div>
            <div class="p-4 z-[1] bg-yellow-400 relative">
              <h3 class="text-base text-black font-medium mb-1 font-serif">{{ otherArtist.name }}</h3>
              <p v-if="otherArtist.craft" class="text-sm text-black/60">{{ otherArtist.craft }}</p>
            </div>
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>

  <!-- 404 State -->
  <div v-else class="min-h-screen flex items-center justify-center bg-white">
    <div class="text-center">
      <h1 class="text-4xl font-serif mb-4">{{ t('pages.artist.notFound.title') }}</h1>
      <NuxtLink to="/collection" class="text-gray-600 hover:text-black underline">
        {{ t('pages.artist.notFound.cta') }}
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useI18n } from '#imports'
import { useArtists, isLightColor } from '~/composables/useArtists'
import CollectionProductCard from '~/components/CollectionProductCard.vue'
import ArtistGallery from '~/components/ArtistGallery.vue'
import { useImageCdn } from '~/composables/useImageCdn'
import { useCdnDiagnostics } from '~/composables/useCdnDiagnostics'
import { useSeo, buildPersonSchema } from '~/composables/useSeo'

const route = useRoute()
const { resolveForDisplay } = useImageCdn()
const { reportCdnError } = useCdnDiagnostics()
const onCdnError = (e: Event, context: string, slug: string | undefined, src?: string) => {
  const resolved = resolveForDisplay(src, slug)
  reportCdnError(context, slug, resolved)
}
const makeErrHandler = (context: string, slug?: string, src?: string): ((e: Event) => void) => {
  return (e: Event) => { onCdnError(e, context, slug, src) }
}
const { getArtistBySlug, getArtistProducts, getOtherArtists, getAllArtists } = useArtists()
const { t } = useI18n()

const slug = computed(() => route.params.slug as string)
const artist = computed(() => {
  const found = getArtistBySlug(slug.value)
  if (!found && import.meta.client) {
    console.warn('Artist not found for slug:', slug.value)
    console.log('Available artists:', getAllArtists().map(a => ({ name: a.name, slug: a.slug })))
  }
  return found
})
const artistProducts = computed(() => {
  if (!artist.value) return []
  return getArtistProducts(artist.value.name, artist.value)
})
const otherArtists = computed(() => {
  if (!artist.value) return []
  return getOtherArtists(artist.value.slug, 6)
})

const activeTab = ref<'collaborations' | 'about'>('collaborations')

// Background color and text contrast
const backgroundColor = computed(() => {
  return artist.value?.backgroundColor || '#ffffff'
})

const isLightBg = computed(() => {
  return isLightColor(backgroundColor.value)
})

const textColorClass = computed(() => {
  return isLightBg.value ? 'text-black' : 'text-white'
})

const textMutedClass = computed(() => {
  return isLightBg.value ? 'text-black/80' : 'text-white/80'
})

const borderColorClass = computed(() => {
  return isLightBg.value ? 'border-black' : 'border-white'
})

const oppositeBgClass = computed(() => {
  return isLightBg.value ? 'bg-white' : 'bg-black'
})

const bgWhiteClass = computed(() => {
  return 'bg-white' // Always white for discover section
})

const activeTabClasses = computed(() => {
  return isLightBg.value 
    ? 'bg-black text-white' 
    : 'bg-white text-black'
})

const inactiveTabClasses = computed(() => {
  return isLightBg.value
    ? 'bg-transparent text-black hover:bg-black/10'
    : 'bg-transparent text-white hover:bg-white/10'
})

const linkHoverClasses = computed(() => {
  return isLightBg.value
    ? 'hover:bg-black hover:text-white'
    : 'hover:bg-white hover:text-black'
})

// Debug on mount
onMounted(() => {
  if (import.meta.client) {
    console.log('Artist page mounted. Slug:', slug.value)
    console.log('Found artist:', artist.value)
    console.log('Artist ID:', artist.value?.id)
    console.log('Artist products (SKUs):', artist.value?.products)
    console.log('Artist products count:', artist.value?.products?.length)
    console.log('Artist products computed:', artistProducts.value)
    console.log('Artist products computed count:', artistProducts.value.length)
  }
})

// Format price helper
const formatPrice = (price: string | number): string => {
  const numPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.]/g, '')) : price
  return numPrice.toFixed(2)
}

const seoDescription = computed(() => {
  if (typeof artist.value?.bio === 'string' && artist.value?.bio) {
    return artist.value.bio
  }
  if (artist.value?.tagline) {
    return artist.value.tagline
  }
  if (artist.value?.name) {
    return t('pages.artist.meta.descriptionFallback', { name: artist.value.name })
  }
  return t('pages.artist.meta.descriptionFallback', { name: t('pages.artist.meta.unknown') })
})

// Build artist image URL for OG/Twitter (use Cloudinary with transformations)
const artistImage = computed(() => {
  if (!artist.value?.photo) return undefined
  const resolved = resolveForDisplay(artist.value.photo, artist.value.slug)
  
  // If it's a Cloudinary public ID, return it as-is (useSeo will add transformations)
  if (resolved.provider === 'cloudinary') {
    return resolved.src
  }
  
  // If it's already a full URL, return as-is
  if (resolved.src.startsWith('http')) {
    return resolved.src
  }
  
  // If it's a local path, make it absolute
  return `https://ommi.shop${resolved.src}`
})

useSeo({
  title: artist.value
    ? t('pages.artist.meta.title', { name: artist.value.name })
    : t('pages.artist.meta.fallbackTitle'),
  description: seoDescription.value,
  keywords: artist.value
    ? `${artist.value.name}, ${artist.value.craft || 'artist'}, ommi.shop, artisan, handmade, ${artist.value.tagline || ''}`
    : 'ommi.shop artist, artisan, handmade',
  type: 'profile',
  image: artistImage.value,
  imageAlt: artist.value ? `${artist.value.name} - Artist on ommi.shop` : 'Artist profile on ommi.shop',
  structuredData: artist.value
    ? buildPersonSchema({
        name: artist.value.name,
        bio: typeof artist.value.bio === 'string' ? artist.value.bio : artist.value.tagline || '',
        tagline: artist.value.tagline,
        photo: artistImage.value,
        links: artist.value.links || [],
        craft: artist.value.craft,
        slug: artist.value.slug
      })
    : undefined
})
</script>
